import type { Questionnaire } from '@medplum/fhirtypes'
import { VALIDATED_TOOL_TAG } from '#src/models/questionnaires/utils/index.ts'
import { QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL } from '#src/structureDefinitions.ts'

export const phq2Questionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/PHQ-2',
  title: 'Patient Health Questionnaire-2 (PHQ-2)',
  name: 'PHQ-2',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    profile: ['http://hl7.org/fhir/4.0/StructureDefinition/Questionnaire'],
    tag: [
      VALIDATED_TOOL_TAG,
      {
        code: 'lformsVersion: 36.7.0',
      },
    ],
  },
  copyright:
    'Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.',
  code: [
    {
      system: 'http://loinc.org',
      code: '55757-9',
      display: 'Patient Health Questionnaire 2 item (PHQ-2) [Reported]',
    },
  ],
  item: [
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'PHQ-2 PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://aidsetc.org/sites/default/files/resources_files/PHQ-2_English.pdf',
        },
      ],
    },
    {
      type: 'choice',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44250-9',
      code: [
        {
          system: 'http://loinc.org',
          code: '44250-9',
          display: 'Little interest or pleasure in doing things',
        },
      ],
      text: 'Little interest or pleasure in doing things',
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6568-5',
            display: 'Not at all',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6569-3',
            display: 'Several days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6570-1',
            display: 'More than half the days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6571-9',
            display: 'Nearly every day',
          },
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44255-8',
          display: 'Feeling down, depressed, or hopeless',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44255-8',
      text: 'Feeling down, depressed, or hopeless',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'decimal',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit',
          valueCoding: {
            system: 'http://unitsofmeasure.org',
            code: '{score}',
            display: '{score}',
          },
        },
      ],
      linkId: '/55758-7',
      code: [
        {
          system: 'http://loinc.org',
          code: '55758-7',
          display:
            'Patient Health Questionnaire 2 item (PHQ-2) total score [Reported]',
        },
      ],
      text: 'Patient Health Questionnaire 2 item (PHQ-2) total score',
    },
  ],
} satisfies Questionnaire

export type Phq2Category =
  | 'Screen Positive (Major Depressive Disorder Likely)'
  | 'Screen Negative'

export function phq2Category(score: number): Phq2Category | undefined {
  if (score >= 3) {
    return 'Screen Positive (Major Depressive Disorder Likely)'
  }
  if (score >= 0) {
    return 'Screen Negative'
  }
  return undefined
}
